import React, { useState } from 'react'
import {
  HelpCircle as HelpCircleIcon,
  Settings as SettingsIcon,
} from 'react-feather'
import { useHistory } from 'react-router-dom'
import clsx from 'clsx'
import { Box, Button, Hidden, Paper, TablePagination } from '@material-ui/core'

import {
  BaseStatusCard,
  Container,
  ContentHeader,
  DriveTour,
  FilterButton,
  NoData2,
  Page,
  Permitted,
} from 'components'
import { TicketFilters, TicketTable } from './components'

import helpers from 'helpers'

import useFetch from 'hooks/useFetch'
import useFilter from 'hooks/useFilter'
import usePagination from 'hooks/usePagination'
import usePermissions from 'hooks/usePermissions'

import theme from 'theme'
import { routes } from 'Routes'
import useStyles from './styles'
import * as service from 'service'
import constants from 'constants/index'

const DataSubjectTicketMain = () => {
  const [openTour, setOpenTour] = useState(false)

  const { TITULAR_STATS_CARD_ITEMS } = constants.tickets
  const { DEMAND_KIND_ID } = constants.questionOptionTickets
  const { TITULAR_TICKETS } = constants.permissions
  const {
    DATA_SUBJECT_MAIN_STEPS_OBJECT,
    DATA_SUBJECT_MAIN_DRIVER_STEP_1,
    DATA_SUBJECT_MAIN_DRIVER_STEP_2,
    DATA_SUBJECT_MAIN_DRIVER_STEP_3,
    DATA_SUBJECT_MAIN_DRIVER_STEP_4,
  } = constants.tours.tickets

  const { handleStatusCard } = helpers.baseStatusCard

  const filter = useFilter({ blacklist: ['page', 'perPage'] })
  const classes = useStyles()
  const history = useHistory()
  const permissions = usePermissions()
  const { perPage, page, handleChangePage, handleChangeRowsPerPage } =
    usePagination(8)

  const canList = permissions.some([
    TITULAR_TICKETS.LIST,
    TITULAR_TICKETS.INTERACT,
  ])

  const { response, isLoading } = useFetch(
    service.dponet.titularTickets.getTitular,
    {
      ...filter.filters,
      page,
      perPage,
    },
    [page, perPage, filter.filters],
    canList,
  )

  const tickets = response?.data?.tickets || []
  const totalCount = response?.data?.meta?.totalCount || 0

  const { companyStatusId, ...statsFilters } = filter.filters
  const statsFiltersKey = JSON.stringify(statsFilters)

  const { response: statsResponse, isLoading: isStatsLoading } = useFetch(
    service.dponet.titularTickets.stats,
    statsFilters,
    [statsFiltersKey],
    canList,
  )

  const stats = statsResponse?.data?.stats || {}

  const { response: answerOptionsResponse, isLoading: isLoadingAnswerOptions } =
    useFetch(
      service.dponet.titularTickets.indexResponsibles,
      { questionOptionTicketKind: DEMAND_KIND_ID },
      [],
      canList,
    )

  const answerOptionTickets =
    answerOptionsResponse?.data?.answerOptionTickets || []

  const { response: usersResponse, isLoading: isLoadingUsers } = useFetch(
    service.dponet.users.get,
    {
      perPage: 10000,
      minimal: true,
      active: true,
      userCompanyStatus: constants.userCompanies.ACTIVE,
    },
  )

  const users = usersResponse?.data?.users || []

  const isLoadingFilter = isLoading || isLoadingAnswerOptions || isLoadingUsers

  const baseStatusCardItems = TITULAR_STATS_CARD_ITEMS.map((item) => ({
    ...item,
    title: stats?.[item.status] ?? 0,
  }))

  const configPermission = permissions.permitted(
    TITULAR_TICKETS.MANAGE_CONFIGURATIONS,
  )

  const filteredSteps = DATA_SUBJECT_MAIN_STEPS_OBJECT.filter(({ element }) => {
    if (!configPermission) {
      return element !== '#' + DATA_SUBJECT_MAIN_DRIVER_STEP_2
    }

    return true
  })

  const handleRedirect = () => history.push(routes.privacyPortal.tickets.config)

  const handleOpenTour = () => {
    const where = 'Solicitação de titulares'
    const params = { action: 'tour-listagem-solicitacao-titulares' }

    helpers.mixpanel.track(where, 'Tour', params)
    setOpenTour(true)
  }

  return (
    <Page title="Solicitações de titulares">
      <Container maxWidth={false}>
        <ContentHeader title="Solicitações de titulares">
          <Button
            variant="contained"
            color="primary"
            startIcon={<HelpCircleIcon size={20} />}
            onClick={handleOpenTour}
            id={DATA_SUBJECT_MAIN_DRIVER_STEP_1}
            fullWidth
          >
            Tutorial
          </Button>
          <Permitted tag={TITULAR_TICKETS.MANAGE_CONFIGURATIONS}>
            <Button
              variant="outlined"
              startIcon={<SettingsIcon size={20} />}
              onClick={handleRedirect}
              id={DATA_SUBJECT_MAIN_DRIVER_STEP_2}
            >
              Configurações
            </Button>
          </Permitted>

          {canList && (
            <FilterButton
              setDrawerOpen={filter.setDrawerOpen}
              fullWidth
              isLoading={isLoadingFilter}
            />
          )}
        </ContentHeader>
        {!canList ? (
          <NoData2
            title="Sem permissão"
            subTitle="Você não tem permissão para visualizar as solicitações de titulares."
          />
        ) : (
          <>
            <Box
              display="flex"
              flexDirection="column"
              gridGap={theme.spacing(2)}
            >
              <Box
                id={DATA_SUBJECT_MAIN_DRIVER_STEP_3}
                className={classes.gridContainer}
              >
                {baseStatusCardItems.map((item, index) => {
                  const isLastItem = index === baseStatusCardItems.length - 1
                  const isSecondLastItem =
                    index === baseStatusCardItems.length - 2

                  return (
                    <Box
                      key={index}
                      className={clsx(classes.gridItems, {
                        [classes.secondLastGridItem]:
                          isLastItem || isSecondLastItem,
                        [classes.lastGridItem]: isLastItem,
                      })}
                    >
                      <BaseStatusCard
                        title={item.title}
                        {...item}
                        selected={
                          filter.filters?.companyStatusId === String(item.id)
                        }
                        onClick={() =>
                          handleStatusCard({
                            filter,
                            value: String(item?.id),
                            key: 'companyStatusId',
                          })
                        }
                        variant="square"
                        titleProps={{ variant: 'h2' }}
                        titleResponse
                        isLoading={isStatsLoading}
                      />
                    </Box>
                  )
                })}
              </Box>
              <Paper id={DATA_SUBJECT_MAIN_DRIVER_STEP_4}>
                <TicketTable isLoading={isLoading} tickets={tickets} />
                {!isLoading && totalCount > 0 && (
                  <TablePagination
                    component="div"
                    count={totalCount}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    page={page - 1}
                    rowsPerPage={perPage}
                    labelRowsPerPage={<Hidden smDown>Por página</Hidden>}
                    rowsPerPageOptions={[4, 8, 16, 32, 64]}
                    nextIconButtonProps={{ size: 'small' }}
                    backIconButtonProps={{ size: 'small' }}
                  />
                )}
              </Paper>
            </Box>
            <TicketFilters
              filter={filter}
              filterFields={filter.filters}
              setFilterFields={filter.setFilters}
              answerOptionTickets={answerOptionTickets}
              users={users}
            />
            <DriveTour
              stepsMatrix={filteredSteps}
              open={openTour}
              setOpen={setOpenTour}
            />
          </>
        )}
      </Container>
    </Page>
  )
}

export default DataSubjectTicketMain
